// PerfilFormSection.js — «Mi perfil» como sección de una página del proyecto:
// los datos de la persona con el header y el pie del sitio alrededor, en vez de
// la pantalla de perfil con pestañas.
//
// El formulario NO se reescribe: es el mismo `DatosPerfilForm` del perfil
// clásico, en su variante de filas, así que los campos salen del `registro_config`
// del proyecto y el guardado es el de siempre.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { profileService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { buildBg } from '../bgHelpers';
import DatosPerfilForm from '../../perfil/DatosPerfilForm';

const Wrapper = styled.section`
  width: 100%;
  padding: ${p => p.$padding || '60px 2rem 80px'};
  background: ${p => p.$bg || '#ffffff'};
`;

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '900px'};
  margin: 0 auto;
  padding: 0 1.5rem;
  box-sizing: border-box;
`;

const PerfilFormSection = ({ config, tl }) => {
  const c = config || {};
  const t = (v, def = '') => (tl ? tl(v, def) : (v || def));
  const { user, updateUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [registroConfig, setRegistroConfig] = useState(null);
  const [convenios, setConvenios] = useState([]);
  const [cargando, setCargando] = useState(true);

  // `profile.php` devuelve en una sola llamada los datos, los campos del
  // registro del proyecto y sus convenios: lo mismo que usa el perfil clásico.
  useEffect(() => {
    if (!user) return;
    let vivo = true;
    profileService.getProfile()
      .then(res => {
        if (!vivo) return;
        setProfile(res.data?.user || null);
        setRegistroConfig(res.data?.registro_config || null);
        setConvenios(res.data?.convenios || []);
      })
      .catch(() => {})
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [user]);

  if (!user) return null;

  return (
    <Wrapper $bg={buildBg(c, '#ffffff')} $padding={c.padding}>
      <Inner $maxWidth={c.ancho_maximo}>
        <DatosPerfilForm
          profile={profile}
          setProfile={setProfile}
          registroConfig={registroConfig}
          convenios={convenios}
          loading={cargando}
          updateUser={updateUser}
          variante={c.variante_formulario || 'filas'}
          campos={Array.isArray(c.campos) ? c.campos : []}
          mostrarTitulo={c.mostrar_titulo === true}
          titulo={t(c.titulo, 'Mis datos')}
          permitirPassword={c.permitir_password !== false}
          estilos={{
            ancho_etiqueta: c.ancho_etiqueta,
            tamano_etiqueta: c.tamano_etiqueta,
            peso_etiqueta: c.peso_etiqueta,
            color_etiqueta: c.color_etiqueta,
            ancho_campo: c.ancho_campo,
            padding_campo: c.padding_campo,
            color_borde_campo: c.color_borde_campo,
            color_borde_foco: c.color_borde_foco,
            radio_campo: c.radio_campo,
            tamano_campo: c.tamano_campo,
            gap_fila: c.gap_fila,
            padding_fila: c.padding_fila,
            color_ayuda: c.color_ayuda,
            tamano_ayuda: c.tamano_ayuda,
            color_separador: c.color_separador,
            margen_separador: c.margen_separador,
            color_boton: c.color_boton,
            color_texto_boton: c.color_texto_boton,
            radio_boton: c.radio_boton,
            padding_boton: c.padding_boton,
            tamano_boton: c.tamano_boton,
            color_boton_password: c.color_boton_password,
            color_texto_boton_password: c.color_texto_boton_password,
            borde_boton_password: c.borde_boton_password,
          }}
          textos={{
            boton_guardar: t(c.texto_boton_guardar, 'Guardar cambios'),
            guardando: t(c.texto_guardando, 'Guardando…'),
            guardado: t(c.texto_guardado, 'Datos actualizados correctamente'),
            cargando: t(c.texto_cargando, 'Cargando datos…'),
            etiqueta_password: t(c.etiqueta_password, 'Contraseña'),
            ayuda_password: t(c.texto_ayuda_password, ''),
            boton_password: t(c.texto_boton_password, 'Cambiar contraseña'),
          }}
        />
      </Inner>
    </Wrapper>
  );
};

export default PerfilFormSection;
